/**
 * Globale und projektbezogene Seitenliste (Paket 14).
 */

import { useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, grundVon, istNetzfehler } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { meldungZu } from "@/lib/meldung";
import { Seitenkopf } from "@/components/Seitenkopf";
import { Leerzustand } from "@/components/Leerzustand";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { SeitenLeseansicht } from "@/components/seiten/SeitenLeseansicht";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { wirksamesProjekt } from "@/lib/projekt-routen";
import { rolleBaumAus } from "@/lib/seitenbaum";
import { useProjekte } from "@/lib/haken";
import {
  seitenTextStatistik,
  ueberschriftenAusMarkdown,
  ueberschriftHref,
} from "@/lib/seiten-inhalt";

export function SeitenStartGlobal() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [titel, setzeTitel] = useState("");
  const [projektId, setzeProjektId] = useState("");
  const [fehler, setzeFehler] = useState<ReturnType<typeof meldungZu> | null>(
    null,
  );

  const { data, isLoading, error } = useQuery({
    queryKey: schluessel.seitenGlobal(),
    queryFn: () => api.seiten.global.query({}),
  });
  const projekte = useProjekte();

  /*
   * Eine Seite gehört immer in ein Projekt — die Projektansicht bekommt es aus
   * der Route, die globale Liste muss danach fragen. Die Regel dazu steht als
   * reine Funktion in `projekt-routen`, damit sie ohne DOM prüfbar bleibt.
   */
  const auswahl = projekte.data ?? [];
  const zielProjekt = wirksamesProjekt(auswahl, projektId);

  const anlegen = useMutation({
    mutationFn: async () => {
      if (!zielProjekt) {
        throw new Error("Bitte zuerst ein Projekt wählen.");
      }
      return api.seiten.anlegen.mutate({
        projektId: zielProjekt,
        titel: titel.trim() || "Neue Seite",
        inhalt: "",
      });
    },
    onSuccess: async (seite) => {
      setzeFehler(null);
      setzeTitel("");
      await client.invalidateQueries({
        queryKey: schluessel.seitenGlobal(),
      });
      await client.invalidateQueries({
        queryKey: schluessel.seitenBaum(seite.projektId),
      });
      await navigate({
        to: "/projekte/$projektId/seiten/$seiteId",
        params: { projektId: seite.projektId, seiteId: seite.id },
      });
    },
    onError: (e) => {
      setzeFehler(
        meldungZu(grundVon(e), {
          netzfehler: istNetzfehler(e),
          fallback:
            e instanceof Error
              ? e.message
              : "Seite konnte nicht angelegt werden.",
        }),
      );
    },
  });

  const anlegeFormular =
    auswahl.length > 0 ? (
      <div className="flex items-center gap-2">
        {auswahl.length > 1 && (
          <Select value={zielProjekt} onValueChange={setzeProjektId}>
            <SelectTrigger className="w-[160px] shrink-0" aria-label="Projekt">
              <SelectValue placeholder="Projekt" />
            </SelectTrigger>
            <SelectContent>
              {auswahl.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.kuerzel}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Input
          value={titel}
          onChange={(e) => setzeTitel(e.target.value)}
          placeholder="Titel der neuen Seite"
          aria-label="Titel der neuen Seite"
          /*
            `Input` bringt `w-full` mit. Eine *Mindest*breite daneben ließ das
            Feld die ganze Kopfzeile nehmen und schob den Knopf in eine dritte
            Zeile. Gedeckelt statt aufgespannt — im Kopf ist das ein
            Schnelleinwurf, kein Formularfeld.
          */
          className="max-w-[15rem]"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              anlegen.mutate();
            }
          }}
        />
        <Button
          type="button"
          onClick={() => anlegen.mutate()}
          disabled={anlegen.isPending || !zielProjekt}
          isLoading={anlegen.isPending}
          data-testid="seite-neu-global"
        >
          Anlegen
        </Button>
      </div>
    ) : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Formular an genau einer Stelle — siehe Begründung in der Projektansicht. */}
      <Seitenkopf
        titel="Seiten"
        beschreibung="Wissensseiten aus allen sichtbaren Projekten."
        {...(data && data.length > 0 ? { aktion: anlegeFormular } : {})}
      />
      {fehler ? (
        <Fehlerzeile
          meldung={fehler}
          onErneut={() => {
            setzeFehler(null);
            anlegen.mutate();
          }}
        />
      ) : null}
      {isLoading && (
        <p className="text-sm text-gray-500" role="status">
          Wird geladen …
        </p>
      )}
      {error && (
        <p className="text-sm text-red-600" role="alert">
          Seiten konnten nicht geladen werden.
        </p>
      )}
      {data && data.length === 0 && (
        <Leerzustand
          titel="Noch keine Seiten"
          /*
           * Ohne sichtbares Projekt hilft keine Aufforderung zum Anlegen —
           * eine Seite braucht eines. Dann sagt der Hinweis, was fehlt,
           * statt auf einen Knopf zu zeigen, den es nicht gibt.
           */
          hinweis={
            auswahl.length > 0
              ? "Gib einen Titel ein und lege die erste Wissensseite an."
              : "Seiten gehören in ein Projekt. Lege zuerst ein Projekt an."
          }
          aktion={anlegeFormular}
        />
      )}
      {data && data.length > 0 && (
        /*
          Dieselbe Zeile wie in Aufgabenliste und Seitenbaum: gestreckter
          Link über die volle Fläche, Anheben beim Überfahren. Das Kürzel
          steht wie dort in Monospace als eigene Spalte, damit die Titel auf
          einer Kante beginnen.
        */
        <ul className="space-y-1">
          {data.map((s) => (
            <li
              key={s.id}
              className="group relative -mx-2 flex min-h-[44px] items-center gap-3 rounded-lg px-2 transition-[background-color,box-shadow] duration-100 ease-aus hover:bg-white hover:shadow-2xs dark:hover:bg-gray-925"
            >
              <span
                className="shrink-0 font-mono text-[11px] text-gray-500 sm:w-14"
                title={s.projektName}
              >
                {s.projektKuerzel}
              </span>
              <Link
                to="/projekte/$projektId/seiten/$seiteId"
                params={{ projektId: s.projektId, seiteId: s.id }}
                className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-gray-900 after:absolute after:inset-0 after:content-[''] dark:text-gray-50"
              >
                {s.titel}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SeitenStartProjekt() {
  const { projektId } = useParams({
    from: "/_app/projekte/$projektId/seiten",
  });
  const navigate = useNavigate();
  const client = useQueryClient();
  const [titel, setzeTitel] = useState("");
  const [fehler, setzeFehler] = useState<ReturnType<typeof meldungZu> | null>(
    null,
  );
  // Welche Seite in der Mitte liegt — ohne Navigieren, der Baum wählt.
  const [gewaehlt, setzeGewaehlt] = useState<string | null>(null);

  const baum = useQuery({
    queryKey: schluessel.seitenBaum(projektId),
    queryFn: () => api.seiten.baum.query({ projektId }),
    enabled: Boolean(projektId),
  });

  const knoten = rolleBaumAus(baum.data ?? []);
  const aktiveId = gewaehlt ?? knoten[0]?.knoten.id ?? null;

  const seite = useQuery({
    queryKey: schluessel.seite(aktiveId ?? ""),
    queryFn: () => api.seiten.lies.query({ id: aktiveId! }),
    enabled: Boolean(aktiveId),
  });

  const marken = useQuery({
    queryKey: schluessel.seitenMarken(aktiveId ?? ""),
    queryFn: () => api.seiten.marken.query({ seiteId: aktiveId! }),
    enabled: Boolean(aktiveId),
  });

  const anlegen = useMutation({
    mutationFn: async () => {
      if (!projektId) {
        throw new Error("Projektkennung fehlt.");
      }
      return api.seiten.anlegen.mutate({
        projektId,
        titel: titel.trim() || "Neue Seite",
        inhalt: "",
      });
    },
    onSuccess: async (angelegt) => {
      setzeFehler(null);
      setzeTitel("");
      await client.invalidateQueries({
        queryKey: schluessel.seitenBaum(projektId),
      });
      await client.invalidateQueries({
        queryKey: schluessel.seitenGlobal(),
      });
      setzeGewaehlt(angelegt.id);
      await navigate({
        to: "/projekte/$projektId/seiten/$seiteId",
        params: { projektId, seiteId: angelegt.id },
      });
    },
    onError: (e) => {
      setzeFehler(
        meldungZu(grundVon(e), {
          netzfehler: istNetzfehler(e),
          fallback:
            e instanceof Error
              ? e.message
              : "Seite konnte nicht angelegt werden.",
        }),
      );
    },
  });

  const anlegeFormular = (
    <div className="flex items-center gap-2">
      <Input
        value={titel}
        onChange={(e) => setzeTitel(e.target.value)}
        placeholder="Titel der neuen Seite"
        aria-label="Titel der neuen Seite"
        className="min-w-[12rem] sm:min-w-[16rem]"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            anlegen.mutate();
          }
        }}
      />
      <Button
        type="button"
        onClick={() => anlegen.mutate()}
        disabled={anlegen.isPending || !projektId}
        isLoading={anlegen.isPending}
        data-testid="seite-neu"
      >
        Anlegen
      </Button>
    </div>
  );

  /*
   * „Auf dieser Seite": die Überschriften des Artikels. Aus dem Markdown
   * gezogen, damit die Randspalte der Seite folgt, statt zu altern.
   */
  const ueberschriften = useMemo(
    () => ueberschriftenAusMarkdown(seite.data?.inhalt ?? ""),
    [seite.data?.inhalt],
  );
  const statistik = useMemo(
    () => seitenTextStatistik(seite.data?.inhalt ?? ""),
    [seite.data?.inhalt],
  );

  if (baum.isLoading) {
    return (
      <div className="px-9 py-14">
        <Mikrolabel grad={11.5}>Wird geladen …</Mikrolabel>
      </div>
    );
  }

  if (baum.isError) {
    return (
      <div className="px-9 py-14" role="alert">
        <Mikrolabel grad={11.5}>
          Seitenbaum konnte nicht geladen werden.
        </Mikrolabel>
      </div>
    );
  }

  if (baum.data && baum.data.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 py-12 sm:px-9">
        {fehler ? <Fehlerzeile meldung={fehler} /> : null}
        <Leerzustand
          titel="Noch keine Seiten in diesem Projekt"
          hinweis="Gib einen Titel ein und lege die erste Wissensseite an."
          aktion={anlegeFormular}
        />
      </div>
    );
  }

  /*
   * `flex-1`, nicht `100dvh` minus Kopfleiste: Der Arbeitsbereich soll genau so
   * hoch sein wie der Rollbereich der Hülle, und den kennt nur die Hülle. Die
   * frühere Rechnung `calc(100dvh - 3.5rem)` stammt aus der Zeit, in der die
   * ganze Seite rollte und die obere Leiste 56px hoch war; seit dem Neuentwurf
   * sind es 60px, und die vier Pixel Differenz waren ein Rollbalken, der ins
   * Leere führte.
   */
  return (
    <div className="grid flex-1 grid-cols-1 items-stretch xl:grid-cols-[240px_minmax(0,1fr)] min-[1720px]:grid-cols-[240px_minmax(0,1fr)_224px]">
      {/* Baum: auf kleinen Breiten als kompakte horizontale Seitenleiste. */}
      <nav className="min-w-0 border-b border-linie bg-papier py-3 xl:border-b-0 xl:border-r xl:py-8">
        <div className="flex items-center justify-between gap-3 px-5 pb-3 xl:px-[22px] xl:pb-4">
          <Mikrolabel grad={12}>Seiten</Mikrolabel>
          <button
            type="button"
            onClick={() => anlegen.mutate()}
            disabled={anlegen.isPending || !projektId}
            className="font-mono text-[11.5px] text-akzent hover:underline disabled:opacity-50"
          >
            + Neue Seite
          </button>
        </div>
        <div className="flex min-w-0 gap-1 overflow-x-auto px-4 pb-1 xl:block xl:px-0">
          {knoten.map(({ knoten: k, tiefe }) => {
            const aktiv = k.id === aktiveId;
            return (
              <button
                type="button"
                key={k.id}
                title={k.titel}
                onClick={() => setzeGewaehlt(k.id)}
                aria-current={aktiv ? "page" : undefined}
                className={[
                  "relative flex h-9 min-w-max cursor-pointer items-center border-none bg-transparent px-3 text-left xl:w-full xl:min-w-0 xl:pr-[18px]",
                  aktiv
                    ? "font-medium text-tinte"
                    : "font-light text-tinte3 hover:bg-wisch hover:text-tinte",
                ].join(" ")}
              >
                {aktiv ? (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-akzent xl:right-auto xl:top-0 xl:h-auto xl:w-[2px]" />
                ) : null}
                <span className="hidden w-[14px] flex-none xl:block" aria-hidden="true" />
                <span
                  className="hidden flex-none xl:block"
                  style={{ width: tiefe * 14 }}
                  aria-hidden="true"
                />
                <span className="max-w-48 truncate text-[13.5px] xl:max-w-none xl:flex-1">
                  {k.titel}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Das Dokument erhält den gesamten verbleibenden Raum. */}
      <main className="flex min-w-0 justify-center px-5 py-10 sm:px-8 sm:py-12 xl:px-10 2xl:px-14">
        <article className="w-full max-w-[860px]">
          {seite.data ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Mikrolabel grad={12} ton="leise">
                  Wissensseite · geändert{" "}
                  {seite.data.geaendertAm
                    ? new Date(seite.data.geaendertAm).toLocaleDateString(
                        "de-DE",
                      )
                    : "—"}
                  {statistik.leseminuten > 0
                    ? ` · ${statistik.leseminuten} Min. Lesezeit`
                    : ""}
                </Mikrolabel>
                {aktiveId ? (
                  <Link
                    to="/projekte/$projektId/seiten/$seiteId"
                    params={{ projektId, seiteId: aktiveId }}
                    className="font-mono text-[11.5px] text-akzent hover:underline min-[1720px]:hidden"
                  >
                    Bearbeiten →
                  </Link>
                ) : null}
              </div>
              <h1 className="mt-5 text-[clamp(2.35rem,5vw,3.5rem)] font-extralight leading-[1.08] tracking-[-0.04em] text-tinte text-balance">
                {seite.data.titel}
              </h1>
              <div className="mt-10 border-t border-tinte" />
              <div className="mt-9 pb-24">
                <SeitenLeseansicht markdown={seite.data.inhalt || ""} />
              </div>
            </>
          ) : seite.isLoading ? (
            <Mikrolabel grad={11.5}>Seite wird geladen …</Mikrolabel>
          ) : (
            <Mikrolabel grad={11.5}>Seite nicht gefunden.</Mikrolabel>
          )}
        </article>
      </main>

      {/* Randinformationen erscheinen erst, wenn die Mitte wirklich Platz hat. */}
      <aside className="hidden border-l border-linie px-6 pb-[72px] pt-12 min-[1720px]:block">
        {aktiveId ? (
          <Link
            to="/projekte/$projektId/seiten/$seiteId"
            params={{ projektId, seiteId: aktiveId }}
            className="mb-8 flex h-[33px] cursor-pointer items-center justify-center bg-blue-600 text-[13px] font-medium text-white transition-colors duration-150 ease-aus hover:bg-blue-700 hover:text-white"
          >
            Bearbeiten
          </Link>
        ) : null}
        {ueberschriften.length > 0 ? (
          <nav aria-label="Auf dieser Seite">
            <Mikrolabel grad={12}>Auf dieser Seite</Mikrolabel>
            <div className="mt-4 flex flex-col">
              {ueberschriften.map((eintrag) => (
                <a
                  key={eintrag.id}
                  href={ueberschriftHref(eintrag.id)}
                  className={[
                    "border-l border-linie py-2 text-[12.5px] leading-snug text-tinte3 hover:border-akzent hover:text-akzent",
                    eintrag.ebene === 3 ? "pl-6" : "pl-3",
                  ].join(" ")}
                >
                  {eintrag.text}
                </a>
              ))}
            </div>
          </nav>
        ) : null}
        <div className="mt-9 border-t border-linie pt-[18px] font-mono text-[11.5px] leading-[2] text-tinte4">
          <div>{(marken.data ?? []).length} Textstellen</div>
          <div>Fassung {seite.data?.version ?? "—"}</div>
          <div>{statistik.woerter} Wörter</div>
        </div>
      </aside>
    </div>
  );
}
